import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { CertidaoSuspensaoPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/certidao-suspensao-pj/certidao-suspensao-pj.service';
import { ReportCertidaoSuspensaoPjRequest, ReportCertidaoSuspensaoPjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/certidao-suspensao-pj/certidao-suspensao-pj.model';
import { CertidaoSuspensaoPjJobService } from '@/core/services/certidao-suspensao-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportCertidaoSuspensaoPjRequest;
type Resp = ReportCertidaoSuspensaoPjResponse;

interface CertidaoSuspensaoPjFormControls {
    assinante: FormControl<string | null>;
    cargoAssinante: FormControl<string | null>;
}

/**
 * Relatório PDF assíncrono: Certidão de Suspensão PJ.
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (nuCrm/sgUf via `PessoaService`) e o assinante/cargo opcionais
 * (texto livre, sem lookup de solicitante), conforme spec `certidao-suspensao.md`.
 */
@Component({
    selector: 'app-certidao-suspensao-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, ReactiveFormsModule, InputTextModule, RelatorioContainer, CanDirective],
    templateUrl: './certidao-suspensao-pj.html',
    styleUrl: './certidao-suspensao-pj.scss'
})
export class ReportCertidaoSuspensaoPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly formBuilder = inject(FormBuilder);
    private readonly certidaoService = inject(CertidaoSuspensaoPjService);
    private readonly pessoaService = inject(PessoaService);
    readonly certidaoJobService = inject(CertidaoSuspensaoPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.certidaoJobService.job$;

    form: FormGroup<CertidaoSuspensaoPjFormControls> = this.formBuilder.group<CertidaoSuspensaoPjFormControls>({
        assinante: new FormControl<string | null>(''),
        cargoAssinante: new FormControl<string | null>('')
    });

    protected get jobFacade(): RelatorioJobFacade {
        return this.certidaoJobService;
    }

    /** Monta o request a partir do prestador selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        this.form.markAllAsTouched();
        if (this.form.invalid) return null;

        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar a certidão.'
            });
            return null;
        }

        const { assinante, cargoAssinante } = this.form.getRawValue();

        return {
            nuCrm,
            sgUf,
            assinante: assinante?.trim() || undefined,
            cargoAssinante: cargoAssinante?.trim() || undefined
        };
    }

    protected exportar(request: Req) {
        return this.certidaoService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.form.reset({ assinante: '', cargoAssinante: '' });
        this.limparJob();
    }
}
